import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import DrawZoneModal from '../HerbicideSchedule/v3/DrawZoneModal'

const ZONE_TYPES = [
  { value: 'turf', label: 'Turf', color: '#2e7d32', fillColor: '#4caf50' },
  { value: 'ornamental', label: 'Ornamental', color: '#9a6b00', fillColor: '#fbc02d' },
  { value: 'bare_ground', label: 'Bare Ground', color: '#c45d00', fillColor: '#fb8c00' }
]

const zoneTypeByValue = new Map(ZONE_TYPES.map(type => [type.value, type]))

const getZoneType = (value) => zoneTypeByValue.get(value) || zoneTypeByValue.get('bare_ground')

const normalizeZone = (zone = {}) => ({
  ...zone,
  sprayType: getZoneType(zone.sprayType).value,
  paths: Array.isArray(zone.paths) ? zone.paths : []
})

const SubcontractorSprayZonesDialog = ({ open, jobsiteId, jobsite, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [zones, setZones] = useState([])
  const [drawZoneOpen, setDrawZoneOpen] = useState(false)
  const [newZoneType, setNewZoneType] = useState('bare_ground')
  const [loadedJobsite, setLoadedJobsite] = useState(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!open || !jobsiteId) return

    let cancelled = false
    setLoading(true)
    setError('')
    setDirty(false)
    axios.get(`/api/subcontractor/owned-jobsites/${jobsiteId}/spray-scope`)
      .then((response) => {
        if (cancelled) return
        setZones((response.data?.sprayScope?.customZones || []).map(normalizeZone))
        setLoadedJobsite(response.data?.jobsite || null)
      })
      .catch((requestError) => {
        if (cancelled) return
        console.error('Failed to load subcontractor spray zones', requestError)
        setError(requestError?.response?.data?.message || 'Unable to load spray zones right now.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [jobsiteId, open])

  const mapCenter = useMemo(() => {
    const source = loadedJobsite || jobsite || {}
    const latitude = Number(source.latitude)
    const longitude = Number(source.longitude)
    return Number.isFinite(latitude) && Number.isFinite(longitude) ? { lat: latitude, lng: longitude } : null
  }, [jobsite, loadedJobsite])

  const existingAreas = useMemo(() => zones.map((zone) => {
    const type = getZoneType(zone.sprayType)
    return {
      ...zone,
      fillColor: type.fillColor,
      strokeColor: type.color
    }
  }), [zones])

  const addZone = (zone) => {
    const type = getZoneType(newZoneType)
    setZones(current => [...current, normalizeZone({
      ...zone,
      label: zone.label === 'Custom Zone' ? `${type.label} Zone` : zone.label,
      sprayType: type.value
    })])
    setDirty(true)
    setDrawZoneOpen(false)
  }

  const removeZone = (index) => {
    setZones(current => current.filter((_, currentIndex) => currentIndex !== index))
    setDirty(true)
  }

  const saveZones = async () => {
    if (!jobsiteId) return
    setSaving(true)
    setError('')
    try {
      await axios.put(`/api/subcontractor/owned-jobsites/${jobsiteId}/spray-scope`, {
        customZones: zones.map(zone => ({
          _id: zone._id,
          paths: zone.paths,
          label: zone.label,
          sprayType: getZoneType(zone.sprayType).value
        }))
      })
      setDirty(false)
      onSaved?.()
      onClose?.()
    } catch (requestError) {
      console.error('Failed to save subcontractor spray zones', requestError)
      setError(requestError?.response?.data?.message || 'Unable to save spray zones right now.')
    } finally {
      setSaving(false)
    }
  }

  const title = loadedJobsite?.siteName || jobsite?.siteName || 'Jobsite'

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Spray zones · {title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Draw each treatment area and assign its application type. These zones are used as this subcontractor-only job’s spray scope.
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
          ) : error && !zones.length ? (
            <Typography variant="body2" color="error">{error}</Typography>
          ) : (
            <>
              <Box display="flex" alignItems="flex-end" style={{ gap: 10, marginBottom: 16 }}>
                <TextField
                  select
                  label="New zone type"
                  value={newZoneType}
                  onChange={(event) => setNewZoneType(event.target.value)}
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 180 }}
                >
                  {ZONE_TYPES.map(type => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
                </TextField>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setDrawZoneOpen(true)}
                  disabled={!mapCenter}
                >
                  Draw zone
                </Button>
              </Box>

              {!mapCenter ? (
                <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
                  This jobsite needs map coordinates before zones can be drawn. Edit the jobsite and save a valid address or latitude/longitude first.
                </Typography>
              ) : null}

              {zones.length ? (
                <Box>
                  {zones.map((zone, index) => {
                    const type = getZoneType(zone.sprayType)
                    return (
                      <Box
                        key={zone._id || `${zone.label}-${index}`}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ padding: '10px 0', borderTop: index ? '1px solid #e7e7e7' : 'none', gap: 12 }}
                      >
                        <Box>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>{zone.label || `${type.label} Zone`}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {Number(zone.areaAcres || 0).toFixed(2)} ac · {zone.paths.length} points
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          <Chip size="small" label={type.label} style={{ background: `${type.fillColor}2b`, color: type.color, fontWeight: 600 }} />
                          <Button size="small" color="secondary" onClick={() => removeZone(index)}>Remove</Button>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              ) : !error ? (
                <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                  No spray zones yet. Choose a type, then draw the first one.
                </Typography>
              ) : null}

              {error && zones.length ? <Typography variant="body2" color="error" style={{ marginTop: 14 }}>{error}</Typography> : null}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={saveZones} disabled={loading || saving || !dirty}>
            {saving ? 'Saving…' : 'Save spray zones'}
          </Button>
        </DialogActions>
      </Dialog>

      {drawZoneOpen && mapCenter ? (
        <DrawZoneModal
          open={drawZoneOpen}
          onClose={() => setDrawZoneOpen(false)}
          onAddZone={addZone}
          existingAreas={existingAreas}
          center={mapCenter}
        />
      ) : null}
    </>
  )
}

export default SubcontractorSprayZonesDialog
